import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Key, ShieldCheck, UserCog, SlidersHorizontal, Search, X, Check, Minus } from "lucide-react";

const ROLES = [
  { value: "superadmin", label: "Super Administrador" },
  { value: "admin_financiero", label: "Administrador Financiero" },
  { value: "operador", label: "Operador" },
];

export default function AdminUsuariosPage() {
  const { token, API, user } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);         // Catálogo de roles RBAC del sistema
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email:"", nombre:"", apellido:"", cedula:"", telefono:"", role:"operador", role_id:"", password:"" });
  const [resetUser, setResetUser] = useState(null);
  const [newPass, setNewPass] = useState("");
  const [rolUser, setRolUser] = useState(null);   // Usuario cuyo rol RBAC estamos asignando
  const [rolSelected, setRolSelected] = useState("");
  // ==== Permisos individuales (extras / denied) ====
  const [permsUser, setPermsUser] = useState(null);
  const [permsInfo, setPermsInfo] = useState(null); // { role, extra_permissions, denied_permissions, effective_permissions }
  const [permsModules, setPermsModules] = useState([]);
  const [extraSet, setExtraSet] = useState(new Set());
  const [deniedSet, setDeniedSet] = useState(new Set());
  const [permsFilter, setPermsFilter] = useState("");
  const [savingPerms, setSavingPerms] = useState(false);

  const load = () => axios.get(`${API}/admin/users`, auth).then(r => setItems(r.data));
  const loadRoles = () => axios.get(`${API}/roles`, auth).then(r => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); loadRoles(); }, []);  // eslint-disable-line

  const roleName = (rid) => (roles.find(r => r.id === rid) || {}).nombre || "—";
  const asignarRol = async () => {
    try {
      await axios.put(`${API}/admin/users/${rolUser.id}/rol`, { role_id: rolSelected || "" }, auth);
      toast.success("Rol asignado");
      setRolUser(null); setRolSelected(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // ==== Permisos individuales ====
  const openPerms = async (u) => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/users/${u.id}/permisos`, auth),
        axios.get(`${API}/roles/modulos`, auth),
      ]);
      setPermsUser(u);
      setPermsInfo(r1.data);
      setPermsModules(r2.data);
      const flatten = (list) => new Set((list || []).flatMap(p => (p.actions || []).map(a => `${p.module}.${a}`)));
      setExtraSet(flatten(r1.data.extra_permissions));
      setDeniedSet(flatten(r1.data.denied_permissions));
      setPermsFilter("");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar permisos"); }
  };

  const cyclePermission = (perm, currentInRole) => {
    // Ciclo: (heredado) → +extra → -denied → (heredado)
    const inExtra = extraSet.has(perm), inDenied = deniedSet.has(perm);
    const nextExtra = new Set(extraSet), nextDenied = new Set(deniedSet);
    if (inExtra) { nextExtra.delete(perm); nextDenied.add(perm); }
    else if (inDenied) { nextDenied.delete(perm); }
    else {
      // heredado → si el rol lo tiene, siguiente estado lógico es "denied"; si no, es "extra"
      if (currentInRole) nextDenied.add(perm);
      else nextExtra.add(perm);
    }
    setExtraSet(nextExtra); setDeniedSet(nextDenied);
  };

  const setPermissionExplicit = (perm, kind) => {
    const nextExtra = new Set(extraSet), nextDenied = new Set(deniedSet);
    nextExtra.delete(perm); nextDenied.delete(perm);
    if (kind === "extra") nextExtra.add(perm);
    else if (kind === "denied") nextDenied.add(perm);
    setExtraSet(nextExtra); setDeniedSet(nextDenied);
  };

  const clearPermsOverrides = () => { setExtraSet(new Set()); setDeniedSet(new Set()); };

  const savePerms = async () => {
    setSavingPerms(true);
    try {
      const groupByModule = (perms) => {
        const map = new Map();
        for (const p of perms) {
          const [m, a] = p.split(".");
          if (!map.has(m)) map.set(m, []);
          map.get(m).push(a);
        }
        return Array.from(map.entries()).map(([module, actions]) => ({ module, actions }));
      };
      await axios.put(`${API}/admin/users/${permsUser.id}/rol`, {
        extra_permissions: groupByModule(Array.from(extraSet)),
        denied_permissions: groupByModule(Array.from(deniedSet)),
      }, auth);
      toast.success("Permisos individuales actualizados");
      setPermsUser(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSavingPerms(false); }
  };

  // Perms del rol base (para saber qué es "heredado")
  const rolePermsSet = new Set(
    (permsInfo?.role?.permissions || []).flatMap(p => (p.actions || []).map(a => `${p.module}.${a}`))
  );
  // Filtrar módulos por búsqueda
  const filteredModules = permsModules
    .map(m => ({
      ...m,
      actions: m.actions.filter(a => {
        if (!permsFilter) return true;
        const q = permsFilter.toLowerCase();
        return m.label.toLowerCase().includes(q) || a.label.toLowerCase().includes(q) || `${m.module}.${a.action}`.includes(q);
      }),
    }))
    .filter(m => m.actions.length > 0);

  const save = async () => {
    try {
      if (editing) {
        const { password, email, ...payload } = form;
        // enviar role_id como null si está vacío para poder "quitar" el rol
        if (!payload.role_id) delete payload.role_id;
        await axios.put(`${API}/admin/users/${editing.id}`, payload, auth);
        toast.success("Actualizado");
      } else {
        const payload = { ...form };
        if (!payload.role_id) delete payload.role_id;
        await axios.post(`${API}/admin/users`, payload, auth);
        toast.success("Usuario creado");
      }
      setOpen(false); setEditing(null); setForm({ email:"", nombre:"", apellido:"", cedula:"", telefono:"", role:"operador", role_id:"", password:"" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const edit = (u) => { setEditing(u); setForm({...u, role_id: u.role_id || "", password:""}); setOpen(true); };
  const del = async (u) => { if (!window.confirm(`¿Eliminar al usuario ${u.email}?`)) return; await axios.delete(`${API}/admin/users/${u.id}`, auth); toast.success("Eliminado"); load(); };
  const resetPass = async () => {
    try { await axios.post(`${API}/admin/users/${resetUser.id}/reset-password`, { new_password: newPass }, auth); toast.success("Contraseña restablecida"); setResetUser(null); setNewPass(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (user?.role !== "superadmin") {
    return <div className="p-8 text-center text-red-600">Acceso restringido al Super Administrador.</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-usuarios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3"><ShieldCheck className="w-8 h-8 text-orange-500" />Usuarios Funcionarios</h1>
          <p className="text-green-700/70 mt-1">Gestión del personal del municipio</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if(!o){ setEditing(null); setForm({ email:"", nombre:"", apellido:"", cedula:"", telefono:"", role:"operador", role_id:"", password:"" }); } }}>
          <DialogTrigger asChild><Button data-testid="btn-add-user" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Nuevo funcionario</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} funcionario</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Email*</Label><Input data-testid="user-email" type="email" disabled={!!editing} value={form.email} onChange={e => setForm({...form, email:e.target.value})} /></div>
              <div><Label>Nombre*</Label><Input data-testid="user-nombre" value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} /></div>
              <div><Label>Apellido*</Label><Input data-testid="user-apellido" value={form.apellido} onChange={e => setForm({...form, apellido:e.target.value})} /></div>
              <div><Label>Cédula</Label><Input value={form.cedula||""} onChange={e => setForm({...form, cedula:e.target.value})} /></div>
              <div><Label>Teléfono</Label><Input value={form.telefono||""} onChange={e => setForm({...form, telefono:e.target.value})} /></div>
              <div className="col-span-2"><Label>Rol*</Label>
                <Select value={form.role_id || ""} onValueChange={v => setForm({...form, role_id: v})}>
                  <SelectTrigger data-testid="user-role-id"><SelectValue placeholder="Seleccione un rol" /></SelectTrigger>
                  <SelectContent>
                    {roles.filter(r => r.is_active !== false).map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.nombre}{r.is_system ? " · sistema" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">Define los módulos y acciones que el usuario podrá ejecutar. <a href="/admin/roles" className="text-green-700 hover:underline">Gestionar roles →</a></p>
              </div>
              {!editing && <div className="col-span-2"><Label>Contraseña inicial*</Label><Input data-testid="user-password" type="password" value={form.password} onChange={e => setForm({...form, password:e.target.value})} /></div>}
            </div>
            <DialogFooter><Button data-testid="btn-save-user" onClick={save} className="bg-green-700 hover:bg-green-800 text-white">{editing ? "Actualizar" : "Crear"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-green-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900"><tr>
              <th className="text-left p-2.5">Nombre</th><th className="text-left p-2.5">Email</th><th className="text-left p-2.5">Cédula</th><th className="text-left p-2.5">Rol</th><th className="text-center p-2.5">Estado</th><th className="text-right p-2.5">Acciones</th>
            </tr></thead>
            <tbody>{items.map(u => (
              <tr key={u.id} className="border-b border-green-50">
                <td className="p-2.5 font-medium">{u.nombre} {u.apellido}</td>
                <td className="p-2.5">{u.email}</td>
                <td className="p-2.5 font-mono">{u.cedula||"-"}</td>
                <td className="p-2.5">{u.role_id ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{roleName(u.role_id)}</Badge> : <Badge variant="outline" className="border-slate-200 text-slate-500">Sin rol</Badge>}</td>
                <td className="p-2.5 text-center">{u.is_active ? <Badge className="bg-green-100 text-green-800">Activo</Badge> : <Badge className="bg-rose-100 text-rose-800">Inactivo</Badge>}</td>
                <td className="p-2.5 text-right">
                  <Button size="icon" variant="ghost" onClick={() => edit(u)} title="Editar"><Edit className="w-4 h-4 text-blue-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => { setRolUser(u); setRolSelected(u.role_id || ""); }} title="Asignar rol RBAC" data-testid={`btn-asignar-rol-${u.id}`}><UserCog className="w-4 h-4 text-orange-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => openPerms(u)} title="Permisos individuales (extras / denied)" data-testid={`btn-perms-${u.id}`}><SlidersHorizontal className="w-4 h-4 text-purple-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setResetUser(u)} title="Resetear contraseña"><Key className="w-4 h-4 text-orange-600" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(u)} title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Restablecer contraseña</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Usuario: <strong>{resetUser?.email}</strong></p>
          <Label>Nueva contraseña</Label>
          <Input data-testid="reset-pass-input" type="password" value={newPass} onChange={e => setNewPass(e.target.value)} />
          <DialogFooter><Button data-testid="btn-reset-pass" onClick={resetPass} className="bg-orange-500 hover:bg-orange-600 text-white">Restablecer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rolUser} onOpenChange={(o) => !o && setRolUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Asignar rol RBAC</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Usuario: <strong>{rolUser?.email}</strong></p>
          <Label>Rol</Label>
          <Select value={rolSelected} onValueChange={setRolSelected}>
            <SelectTrigger data-testid="rol-select"><SelectValue placeholder="Seleccione rol" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sin rol asignado</SelectItem>
              {roles.filter(r => r.is_active !== false).map(r => (
                <SelectItem key={r.id} value={r.id}>{r.nombre}{r.is_system ? " · sistema" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-2">
            <a href="/admin/roles" className="text-green-700 hover:underline">Gestionar roles y permisos →</a>
          </p>
          <DialogFooter><Button data-testid="btn-save-rol" onClick={() => { const rs = rolSelected === "__none__" ? "" : rolSelected; setRolSelected(rs); asignarRol(); }} className="bg-green-700 hover:bg-green-800 text-white">Asignar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DIÁLOGO: PERMISOS INDIVIDUALES (extras / denied) ============ */}
      <Dialog open={!!permsUser} onOpenChange={(o) => { if (!o) setPermsUser(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="perms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-800">
              <SlidersHorizontal className="w-5 h-5" /> Permisos individuales
            </DialogTitle>
            <p className="text-sm text-slate-600 mt-1">
              Ajuste los permisos <strong>por encima</strong> o <strong>por debajo</strong> del rol asignado.
              Estos overrides se aplican SOLO a este usuario: <strong>{permsUser?.email}</strong>.
            </p>
          </DialogHeader>

          {/* Resumen */}
          <div className="grid grid-cols-3 gap-2 my-2 flex-shrink-0">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
              <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Rol base</div>
              <div className="text-sm font-bold text-green-900 mt-0.5">{permsInfo?.role?.nombre || "Sin rol"}</div>
            </div>
            <div className="rounded-lg bg-green-50 border border-green-200 p-2 text-center">
              <div className="text-[10px] uppercase font-bold text-green-700 tracking-widest">+ Extras concedidos</div>
              <div className="text-sm font-bold text-green-800 mt-0.5" data-testid="perms-extras-count">{extraSet.size}</div>
            </div>
            <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-center">
              <div className="text-[10px] uppercase font-bold text-red-700 tracking-widest">– Denegados</div>
              <div className="text-sm font-bold text-red-800 mt-0.5" data-testid="perms-denied-count">{deniedSet.size}</div>
            </div>
          </div>

          {/* Buscador */}
          <div className="flex items-center gap-2 flex-shrink-0 mb-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar módulo o acción…" value={permsFilter} onChange={e => setPermsFilter(e.target.value)}
                     className="pl-9" data-testid="perms-search" />
            </div>
            <Button variant="outline" size="sm" onClick={clearPermsOverrides} data-testid="btn-clear-overrides">
              <X className="w-3.5 h-3.5 mr-1" /> Limpiar overrides
            </Button>
          </div>

          {/* Leyenda */}
          <div className="flex flex-wrap gap-3 text-[11px] flex-shrink-0 mb-2 text-slate-600">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Heredado del rol</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-600 inline-block" /> Extra concedido a este usuario</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Denegado a este usuario</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-slate-300 inline-block" /> Sin acceso</span>
          </div>

          {/* Matriz de módulos */}
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg" data-testid="perms-matrix">
            {filteredModules.length === 0 && (
              <div className="p-10 text-center text-slate-500 text-sm">Sin resultados</div>
            )}
            {filteredModules.map(m => (
              <div key={m.module} className="border-b border-slate-100 last:border-b-0">
                <div className="bg-slate-50 px-3 py-2 sticky top-0 z-10">
                  <div className="font-bold text-green-900 text-sm">{m.label}</div>
                  <div className="text-[10px] font-mono text-slate-500">{m.module}</div>
                </div>
                {m.actions.map(a => {
                  const perm = `${m.module}.${a.action}`;
                  const inRole = rolePermsSet.has(perm);
                  const inExtra = extraSet.has(perm);
                  const inDenied = deniedSet.has(perm);
                  const effective = (inRole && !inDenied) || inExtra;
                  let state = "none";
                  if (inExtra) state = "extra";
                  else if (inDenied) state = "denied";
                  else if (inRole) state = "role";
                  const dotColor = state === "extra" ? "bg-emerald-600"
                                  : state === "denied" ? "bg-red-500"
                                  : state === "role" ? "bg-green-500"
                                  : "bg-slate-300";
                  return (
                    <div key={perm} className={`flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 ${effective ? "" : "opacity-70"}`} data-testid={`perm-row-${perm}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${dotColor} flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800 truncate">{a.label}</div>
                        <div className="text-[10px] font-mono text-slate-400 truncate">{perm}{inRole ? " · del rol" : ""}</div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm" variant={inExtra ? "default" : "outline"}
                          className={`h-7 px-2 text-xs ${inExtra ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"}`}
                          onClick={() => setPermissionExplicit(perm, inExtra ? "reset" : "extra")}
                          data-testid={`perm-extra-${perm}`}
                          disabled={inRole}
                          title={inRole ? "Ya está en el rol base" : (inExtra ? "Quitar override" : "Conceder extra")}
                        >
                          <Check className="w-3 h-3 mr-0.5" /> +
                        </Button>
                        <Button
                          size="sm" variant={inDenied ? "default" : "outline"}
                          className={`h-7 px-2 text-xs ${inDenied ? "bg-red-500 hover:bg-red-600 text-white" : "text-red-700 border-red-200 hover:bg-red-50"}`}
                          onClick={() => setPermissionExplicit(perm, inDenied ? "reset" : "denied")}
                          data-testid={`perm-denied-${perm}`}
                          disabled={!inRole && !inExtra}
                          title={(!inRole && !inExtra) ? "No hay nada que denegar" : (inDenied ? "Quitar override" : "Denegar este permiso")}
                        >
                          <Minus className="w-3 h-3 mr-0.5" /> –
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <DialogFooter className="flex-shrink-0 mt-3">
            <Button variant="outline" onClick={() => setPermsUser(null)}>Cancelar</Button>
            <Button
              onClick={savePerms}
              disabled={savingPerms}
              className="bg-purple-700 hover:bg-purple-800 text-white"
              data-testid="btn-save-perms"
            >
              {savingPerms ? "Guardando…" : `Guardar (+${extraSet.size} / –${deniedSet.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
